import { Dispatch, SetStateAction, useContext } from 'react';
import { CellLocation, Id, MenuOption, SelectionMode } from '@silevis/reactgrid';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { TableContext } from 'src/app/providers/TableProvider';
import { UserContext } from 'src/app/providers/UserProvider';
import TableService from 'src/shared/api/table/TableService';
import { saveByteArray, setError } from 'src/shared/lib/utils';
import { canAccessGroups, canManageModel, canWriteData } from 'src/shared/store/user/permissions';

export type TableCellMetaMode = 'comment' | 'history';

export type TableCellMetaTarget = {
  rowIndex: number;
  rowId: number;
  columnName: string;
};

interface UseContextMenuProps {
  canHideIdColumn: boolean;
  isIdHidden: boolean;
  setIsIdHidden: Dispatch<SetStateAction<boolean>>;
  setIsOpenEditModal: Dispatch<SetStateAction<boolean>>;
  setIsOpenWorkflowModal: Dispatch<SetStateAction<boolean>>;
  setIsOpenBusinessRulesModal: Dispatch<SetStateAction<boolean>>;
  setIsOpenTotalsRowModal: Dispatch<SetStateAction<boolean>>;
  setIsOpenConsolidationModal: Dispatch<SetStateAction<boolean>>;
  setIsOpenImportModal: Dispatch<SetStateAction<boolean>>;
  setIsOpenSubsetModal: Dispatch<SetStateAction<boolean>>;
  onOpenCellMetaModal: (target: TableCellMetaTarget, mode: TableCellMetaMode) => void;
  onOpenColumnAccessModal: (columnName: string) => void;
}

export const useContextMenu = ({
  canHideIdColumn,
  isIdHidden,
  setIsIdHidden,
  setIsOpenEditModal,
  setIsOpenWorkflowModal,
  setIsOpenBusinessRulesModal,
  setIsOpenTotalsRowModal,
  setIsOpenConsolidationModal,
  setIsOpenImportModal,
  setIsOpenSubsetModal,
  onOpenCellMetaModal,
  onOpenColumnAccessModal,
}: UseContextMenuProps) => {
  const { setIsEdit, addEmptyRows, addEmptyTopRows, isEdit, setTableData, tableData } =
    useContext(TableContext);
  const { selectedForm, tableSettings } = useContext(ProjectsContext);
  const { user } = useContext(UserContext);
  const canManage = canManageModel(user);
  const canManageAccess = canAccessGroups(user);
  const canWrite = canWriteData(user) && !tableSettings?.workflowReadOnly;
  const hasConfiguredTotalsRow = Boolean(
    tableSettings?.viewSettings?.totalsRow?.enabled &&
      tableSettings?.viewSettings?.totalsRow?.columns?.length,
  );

  const resolveSelectedCell = (
    selectedRanges: Array<CellLocation[]>,
  ): TableCellMetaTarget | null => {
    const selectedCell = selectedRanges?.[0]?.[0];
    if (!selectedCell) {
      return null;
    }

    if (typeof selectedCell.rowId !== 'number' || typeof selectedCell.columnId !== 'string') {
      return null;
    }

    const rowIndex = selectedCell.rowId;
    const rowData = tableData?.[rowIndex];
    if (!rowData || typeof rowData.id !== 'number') {
      return null;
    }

    if (selectedCell.columnId.toLowerCase() === 'actions') {
      return null;
    }

    return {
      rowIndex,
      rowId: rowData.id,
      columnName: selectedCell.columnId,
    };
  };

  const handleExport = async () => {
    if (!selectedForm?.id) {
      setError('Не указан ID таблицы');
      return;
    }

    try {
      const blob = await TableService.exportExcel(selectedForm.id);
      saveByteArray(blob, `table_${selectedForm.id}.xlsx`);
    } catch (error) {
      console.error('Не удалось выгрузить файл:', error);
      setError('Не удалось выгрузить файл');
    }
  };

  const editMenu = (rows: number[]): MenuOption[] => [
    {
      id: 'addRowBottom',
      label: 'Добавить строку ниже',
      handler: () => addEmptyRows(1),
    },
    {
      id: 'addRowTop',
      label: 'Добавить строку выше',
      handler: () => addEmptyTopRows(),
    },
    {
      id: 'removeRow',
      label: rows.length > 1 ? 'Удалить строки' : 'Удалить строку',
      handler: () => {
        if (rows.length) {
          setTableData(prev => prev.filter((_, index) => !rows.includes(index)));
        }
      },
    },
  ];

  const baseMenu: MenuOption[] = [
    ...(canHideIdColumn
      ? [
          {
            id: 'id',
            label: isIdHidden ? 'Показать ID' : 'Скрыть ID',
            handler: () => setIsIdHidden(prev => !prev),
          },
        ]
      : []),
    {
      id: 'export',
      label: 'Экспорт в Excel',
      handler: handleExport,
    },
  ];

  const writeMenu: MenuOption[] = [
    {
      id: 'edit',
      label: 'Режим редактирования',
      handler: () => setIsEdit(prev => !prev),
    },
    {
      id: 'import',
      label: 'Импорт из Excel',
      handler: () => setIsOpenImportModal(prev => !prev),
    },
    {
      id: 'bulkSubset',
      label: 'Массовая операция по подмножеству',
      handler: () => setIsOpenSubsetModal(true),
    },
  ];

  const cellMenu = (target: TableCellMetaTarget | null): MenuOption[] => {
    if (!target) {
      return [];
    }

    return [
      {
        id: 'cellComment',
        label: 'Комментарий ячейки',
        handler: () => onOpenCellMetaModal(target, 'comment'),
      },
      {
        id: 'cellHistory',
        label: 'История ячейки',
        handler: () => onOpenCellMetaModal(target, 'history'),
      },
    ];
  };

  const columnAccessMenu = (target: TableCellMetaTarget | null): MenuOption[] => {
    if (!target || !canManageAccess) {
      return [];
    }

    return [
      {
        id: 'configureColumnAccess',
        label: 'Настроить права доступа',
        handler: () => onOpenColumnAccessModal(target.columnName),
      },
    ];
  };

  const modelManagementMenu: MenuOption[] = [
    {
      id: 'editTable',
      label: 'Редактировать форму',
      handler: () => setIsOpenEditModal(true),
    },
  ];

  const workflowManagementMenu: MenuOption[] =
    canManage && !tableSettings?.isDirectory
      ? [
          {
            id: 'configureWorkflow',
            label: 'Настроить бизнес-процесс',
            handler: () => setIsOpenWorkflowModal(true),
          },
        ]
      : [];

  const businessRulesManagementMenu: MenuOption[] = canManage
    ? [
        {
          id: 'configureExpression',
          label: 'Настроить выражение',
          handler: () => setIsOpenBusinessRulesModal(true),
        },
        {
          id: 'configureTotalsRow',
          label: hasConfiguredTotalsRow ? 'Редактировать строку итогов' : 'Добавить строку итогов',
          handler: () => setIsOpenTotalsRowModal(true),
        },
        {
          id: 'configureConsolidation',
          label: 'Консолидация',
          handler: () => setIsOpenConsolidationModal(true),
        },
      ]
    : [];

  const getMainMenu = (target: TableCellMetaTarget | null): MenuOption[] => {
    const menu: MenuOption[] = [];

    if (canManage) {
      menu.push(...modelManagementMenu, ...workflowManagementMenu, ...businessRulesManagementMenu);
    }
    if (canWrite) {
      menu.push(...writeMenu);
    }

    menu.push(...baseMenu, ...cellMenu(target), ...columnAccessMenu(target));
    return menu;
  };

  const simpleHandleContextMenu = (
    _selectedRowIds: Id[],
    _selectedColIds: Id[],
    _selectionMode: SelectionMode,
    _menuOptions: MenuOption[],
    selectedRanges: Array<CellLocation[]>,
  ): MenuOption[] => {
    const targetCell = resolveSelectedCell(selectedRanges);

    if (isEdit) {
      const selectedRows: number[] =
        [...new Set(selectedRanges?.[0]?.map(row => row.rowId as number))] || [];

      const editModeMenu: MenuOption[] = [
        ...workflowManagementMenu,
        ...businessRulesManagementMenu,
        ...columnAccessMenu(targetCell),
      ];

      if (canWrite) {
        editModeMenu.push(...editMenu(selectedRows));
      }

      if (editModeMenu.length) {
        return editModeMenu;
      }
    }

    return getMainMenu(targetCell);
  };

  return { simpleHandleContextMenu };
};
